import { useCallback, Dispatch, SetStateAction } from "react";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { IResolveParams } from "reactjs-social-login";
import { useCustomRegisterSocialLogin } from "../../../../hooks/users";
import {
  SocialAuthResponse,
  RegistrationType,
  RegistrationStep,
} from "../types";
import { SOCIAL_LOGIN_REFUSALS } from "../registrationRefusals";
import { refusalMessageKey } from "../../../../utils/refusalMessageKey";

interface UseSocialAuthProps {
  entityIdentification?: string;
  form_id?: string;
  setCurrentStep: Dispatch<SetStateAction<RegistrationStep>>;
  setType: Dispatch<SetStateAction<RegistrationType>>;
  setSocialResponse: Dispatch<SetStateAction<SocialAuthResponse | null>>;
  setEmail: (email: string) => void;
  getStepNumber: (
    logicalStep: "auth" | "form" | "questions"
  ) => RegistrationStep;
  hasCustomQuestions: boolean;
  showQuestionsFirst: boolean;
  setIsExistingUser?: Dispatch<SetStateAction<boolean>>;
}

export const useSocialAuth = ({
  entityIdentification,
  form_id,
  setCurrentStep,
  setType,
  setSocialResponse,
  setEmail,
  getStepNumber,
  hasCustomQuestions,
  showQuestionsFirst,
  setIsExistingUser,
}: UseSocialAuthProps) => {
  const { t } = useTranslation("clientView");
  const { mutate: socialLogin } = useCustomRegisterSocialLogin();

  const handleLoginStart = useCallback(() => {}, []);

  const handleReject = useCallback(
    (err: any) => {
      console.error("Social auth error:", err);
      toast.error(t("customRegistration.page.toast.unexpectedError"));
    },
    [t]
  );

  const handleResolve = useCallback(
    (provider: IResolveParams["provider"], data: IResolveParams["data"]) => {
      const cpPayload = {
        access_token:
          provider === "google"
            ? data?.access_token
            : data?.authorization?.id_token,
        provider,
      };

      socialLogin(
        {
          entity_identification: entityIdentification,
          form_id,
          data: { type: "login", providerPayload: cpPayload },
        },
        {
          onSuccess: (response) => {
            const element = response?.data?.element;
            if (element?.user_identification && element?.access_token) {
              const isNewUser =
                response.status === 201 && response?.data?.code === "RSUSOK005";
              if (!isNewUser) {
                setIsExistingUser?.(true);
              }
              const nextStep = getStepNumber("form");
              setCurrentStep(nextStep);
              setType("social");
              setSocialResponse(element);
              setEmail(element?.user_email || "");
            } else {
              toast.error(t("customRegistration.page.toast.unexpectedError"));
            }
          },
          onError: (err: any) => {
            console.error("Social login error:", err);
            toast.error(
              t(
                refusalMessageKey(
                  err,
                  SOCIAL_LOGIN_REFUSALS,
                  "customRegistration.page.toast.socialLoginFailed"
                )
              )
            );
          },
        }
      );
    },
    [
      entityIdentification,
      form_id,
      socialLogin,
      setCurrentStep,
      setType,
      setSocialResponse,
      setEmail,
      t,
      hasCustomQuestions,
      showQuestionsFirst,
      getStepNumber,
    ]
  );

  return {
    handleLoginStart,
    handleReject,
    handleResolve,
  };
};
